import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Breadcrumbs, type Crumb } from "./Breadcrumbs";
import { Faq, type FaqEntry } from "./Faq";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { rich } from "./RichText";
import { PROCEDURES, type Block, type RelatedLink } from "@/content/inner-pages";
import portrait2 from "@/assets/kk-portrait-2.webp";
import portrait3 from "@/assets/kk-portrait-3.webp";
import proc1 from "@/assets/proc-1.webp";

export const WA_BASE = "https://wa.me/5545999175813";
export const waLink = (msg: string) => `${WA_BASE}?text=${encodeURIComponent(msg)}`;

export const slugifyHeading = (text: string) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const FALLBACK_RELATED: Record<string, { text: string; image: string; position?: string }> = {
  "/especialidades": {
    text: "Todas as cirurgias plásticas realizadas em Foz do Iguaçu.",
    image: proc1,
  },
  "/dr-kelson-kawamura": {
    text: "Formação, experiência e filosofia de atendimento do cirurgião.",
    image: portrait2,
    position: "center 15%",
  },
  "/primeira-consulta-cirurgia-plastica": {
    text: "Como funciona a avaliação inicial, etapa por etapa.",
    image: portrait3,
    position: "center 15%",
  },
  "/consultorio-cirurgia-plastica-foz-do-iguacu": {
    text: "Estrutura, localização central e contato para agendamento.",
    image: portrait2,
    position: "center 15%",
  },
};

export function resolveRelated(r: RelatedLink): { text?: string; image?: string; position?: string } {
  const proc = PROCEDURES.find((p) => p.path === r.to);
  const fallback = FALLBACK_RELATED[r.to];
  return {
    text: r.text ?? proc?.cardText ?? fallback?.text,
    image: r.image ?? proc?.image ?? fallback?.image,
    position: r.image ? undefined : fallback?.position,
  };
}

export type ContentPageProps = {
  eyebrow: string;
  h1: string;
  heroTitle: string;
  lead: string;
  image?: string;
  imageAlt?: string;
  crumbs: Crumb[];
  blocks: Block[];
  related?: RelatedLink[];
  relatedTitle?: string;
  articles?: { label: string; to: string }[];
  faq?: FaqEntry[];
  ctaLabel: string;
  ctaHref: string;
  ctaSecondary?: { label: string; href: string };
  extra?: ReactNode;
  afterHero?: ReactNode;
  leadExtra?: ReactNode;
  heroAside?: ReactNode;
  hideSideNav?: boolean;
};


export function ContentPage({
  eyebrow,
  h1,
  heroTitle,
  lead,
  image,
  imageAlt,
  crumbs,
  blocks,
  related,
  relatedTitle = "Conteúdos relacionados",
  articles,
  faq,
  ctaLabel,
  ctaHref,
  ctaSecondary,
  extra,
  afterHero,
  leadExtra,
  heroAside,
  hideSideNav,
}: ContentPageProps) {
  const sections = blocks.map((b) => ({ ...b, id: slugifyHeading(b.h) }));


  return (
    <>
      {/* HERO */}
      <section className="kk-section kk-gradient-hero kk-inner-hero">
        <div className="kk-container kk-inner-hero__grid kk-fade-up">
          <div>
            <span className="kk-eyebrow">{eyebrow}</span>
            <h1 className="kk-h1 kk-h1--inner">{h1}</h1>
            <hr className="kk-divider" />
            <p className="kk-inner-hero__subtitle">{heroTitle}</p>
            <p className="kk-lead">{rich(lead)}</p>
            {leadExtra}
            <div className="kk-hero__cta-row">
              <a href={ctaHref} target="_blank" rel="noopener noreferrer" className="kk-button-primary">
                {ctaLabel} <WhatsAppIcon />
              </a>
              {ctaSecondary ? (
                <a
                  href={ctaSecondary.href}
                  target={ctaSecondary.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="kk-button-secondary"
                >
                  {ctaSecondary.label}
                </a>
              ) : null}
            </div>
          </div>
          {heroAside ? (
            heroAside
          ) : image ? (
            <div className="kk-image-editorial kk-image-editorial--inner">
              <img src={image} alt={imageAlt ?? h1} fetchPriority="high" decoding="async" />
            </div>
          ) : null}
        </div>
      </section>

      <Breadcrumbs items={crumbs} />

      {afterHero}

      {/* CONTEÚDO */}
      <section className="kk-section">
        <div className={`kk-container${hideSideNav ? "" : " kk-article-grid"}`}>
          {hideSideNav ? null : (
            <aside className="kk-side-nav" aria-label="Nesta página">
              <div className="kk-side-nav__inner">
                <span className="kk-eyebrow">Nesta página</span>
                <ul>
                  {sections.map((s) => (
                    <li key={s.id}>
                      <a href={`#${s.id}`}>{s.h}</a>
                    </li>
                  ))}
                </ul>
                <a href={ctaHref} target="_blank" rel="noopener noreferrer" className="kk-button-primary kk-side-nav__cta">
                  {ctaLabel} <WhatsAppIcon size={16} />
                </a>
              </div>
            </aside>
          )}


          <div className={`kk-article${hideSideNav ? " kk-article--full" : ""}`}>
            {sections.map((s) => (
              <div className="kk-article__block" id={s.id} key={s.id}>
                <h2 className="kk-h2 kk-h2--article">{s.h}</h2>
                {s.p?.map((text) => (
                  <p className="kk-text" key={text.slice(0, 40)}>
                    {rich(text)}
                  </p>
                ))}
                {s.list ? (
                  <ul className="kk-bullets">
                    {s.list.map((li) => (
                      <li key={li}>{rich(li)}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}

            {extra}

            {related?.length ? (
              <div className="kk-article__block">
                <h2 className="kk-h2 kk-h2--article">{relatedTitle}</h2>
                <div className="kk-related">
                  {related.slice(0, 3).map((r) => {
                    const info = resolveRelated(r);
                    return (
                      <Link key={r.to} to={r.to} className="kk-related__card">
                        {info.image ? (
                          <span className="kk-related__media">
                            <img src={info.image} alt={r.label} loading="lazy" decoding="async" style={info.position ? { objectPosition: info.position } : undefined} />
                          </span>
                        ) : null}
                        <span className="kk-related__body">
                          <span className="kk-related__label">
                            {r.label}
                            <span aria-hidden="true">→</span>
                          </span>
                          {info.text ? <span className="kk-related__text">{info.text}</span> : null}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {articles?.length ? (
              <div className="kk-article__block">
                <h2 className="kk-h2 kk-h2--article">Leia também no blog</h2>
                <ul className="kk-article-links">
                  {articles.map((a) => (
                    <li key={a.to}>
                      <Link to={a.to}>{a.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}



          </div>
        </div>
      </section>

      {faq?.length ? (
        <section className="kk-section kk-section--alt">
          <div className="kk-container">
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <span className="kk-eyebrow">Dúvidas</span>
              <h2 className="kk-h2">Perguntas frequentes</h2>
            </div>
            <Faq items={faq} />
          </div>
        </section>
      ) : null}

      {/* CTA FINAL */}
      <section className="kk-cta-fullwidth">
        <div className="kk-container" style={{ textAlign: "center" }}>
          <h2 className="kk-h2">Converse com a equipe</h2>
          <p className="kk-lead" style={{ margin: "0 auto 28px" }}>
            Agende uma <strong>avaliação individualizada</strong> para entender possibilidades, limites, cuidados e próximos passos.
          </p>
          <a href={ctaHref} target="_blank" rel="noopener noreferrer" className="kk-button-primary">
            {ctaLabel} <WhatsAppIcon />
          </a>
        </div>
      </section>
    </>
  );
}
